import type { McpResult } from '@/mcp/types'
import { useStudioStore } from '@/store/studio-store'
import type { StudioCapabilityImpactPreview } from './studio-capability-gateway'

/**
 * Surface capability-gateway confirmation plans through the shared Studio
 * Chat rail. Both natural-language Chat and the public WebMCP bridge use this
 * helper so a write operation always reaches the same user-confirmation UI.
 */
export function surfaceStudioCapabilityGatewayResult(result: McpResult): McpResult {
  if (!result.success || !result.data || typeof result.data !== 'object') {
    return result
  }

  const data = result.data as Record<string, unknown>
  if (data.status === 'failed') {
    return {
      success: false,
      error:
        typeof data.error === 'string'
          ? data.error
          : 'Studio capability execution failed.',
    }
  }

  if (
    data.status === 'confirmation-required' &&
    typeof data.confirmationId === 'string'
  ) {
    const state = useStudioStore.getState()
    // Raw WebMCP can arrive while the intent loop is collapsed. Approval is a
    // user decision, so make the shared confirmation surface visible instead
    // of leaving a valid plan hidden until it expires.
    state.setChatSidebarOpen(true)
    const alreadyVisible = state.chatMessages.some(
      (message) =>
        message.kind === 'capability-confirmation' &&
        message.confirmationId === data.confirmationId,
    )
    if (!alreadyVisible) {
      state.appendChatMessage({
        kind: 'capability-confirmation',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        confirmationId: data.confirmationId,
        title:
          typeof data.label === 'string' ? data.label : 'Confirm Studio change',
        tools: Array.isArray(data.tools)
          ? data.tools.filter((tool): tool is string => typeof tool === 'string')
          : [],
        changedPaths: Array.isArray(data.changedPaths)
          ? data.changedPaths.filter(
              (path): path is string => typeof path === 'string',
            )
          : [],
        expiresAt:
          typeof data.expiresAt === 'string'
            ? data.expiresAt
            : new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        impact:
          data.impact && typeof data.impact === 'object'
            ? (data.impact as StudioCapabilityImpactPreview)
            : undefined,
      })
    }
  }

  return result
}
